import { API_BASE } from '@/shared/services/api';
import { useTranslation } from "react-i18next";import React, { useState, useEffect } from 'react';
import {
  Search,
  Plus,
  Download,
  Filter,
  Package,
  Truck,
  Clock,
  CheckCircle,
  AlertCircle,
  FileText,
  Receipt,
  Network,
  FileSpreadsheet,
  Edit,
  MapPin,
  Calendar,
  ChevronRight,
  Eye,
  Thermometer,
  MoreHorizontal,
  ScanLine,
  Loader2 } from
'lucide-react';
import { Button } from '@/shared/components/ui/button';
import { Input } from '@/shared/components/ui/input';
import { Badge } from '@/shared/components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue } from
"@/shared/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle } from
"@/shared/components/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger } from
"@/shared/components/ui/dropdown-menu";
import { cn } from '@/shared/utils';

const API_URL = API_BASE;

const asnStatuses = [
{ value: 'DRAFT', label: 'Draft' },
{ value: 'CONFIRMED', label: 'Confirmed' },
{ value: 'IN_TRANSIT', label: 'In Transit' },
{ value: 'ARRIVED', label: 'Arrived' },
{ value: 'RECEIVING', label: 'Receiving' },
{ value: 'RECEIVED', label: 'Received' },
{ value: 'RECEIVED_PARTIAL', label: 'Partial Receipt' },
{ value: 'CLOSED', label: 'Closed' },
{ value: 'CANCELLED', label: 'Cancelled' }];


const asnSources = [
{ value: 'PO', label: 'Purchase Order' },
{ value: 'NFE', label: 'NF-e' },
{ value: 'CTE', label: 'CT-e' },
{ value: 'EDI', label: 'EDI' },
{ value: 'CSV', label: 'CSV/TXT' },
{ value: 'MANUAL', label: 'Manual' }];


const statusConfig = {
  DRAFT: { color: 'bg-gray-100 text-gray-700 border-gray-200', icon: FileText },
  CONFIRMED: { color: 'bg-blue-50 text-blue-700 border-blue-200', icon: CheckCircle },
  IN_TRANSIT: { color: 'bg-violet-50 text-violet-700 border-violet-200', icon: Truck },
  ARRIVED: { color: 'bg-amber-50 text-amber-700 border-amber-200', icon: MapPin },
  RECEIVING: { color: 'bg-cyan-50 text-cyan-700 border-cyan-200', icon: ScanLine },
  RECEIVED: { color: 'bg-emerald-50 text-emerald-700 border-emerald-200', icon: CheckCircle },
  RECEIVED_PARTIAL: { color: 'bg-orange-50 text-orange-700 border-orange-200', icon: AlertCircle },
  CLOSED: { color: 'bg-gray-100 text-gray-600 border-gray-200', icon: CheckCircle },
  CANCELLED: { color: 'bg-red-50 text-red-700 border-red-200', icon: AlertCircle }
};

const sourceIcons = {
  PO: FileText,
  NFE: Receipt,
  CTE: Truck,
  EDI: Network,
  CSV: FileSpreadsheet,
  MANUAL: Edit
};

const ASNCard = ({ asn, onView, onStartReceiving }) => {const { t } = useTranslation("oig");
  const status = statusConfig[asn.status] || statusConfig.DRAFT;
  const StatusIcon = status.icon;
  const statusLabel = t("asn_status_" + asn.status, asnStatuses.find((s) => s.value === asn.status)?.label || asn.status);
  const SourceIcon = sourceIcons[asn.source] || FileText;
  const sourceLabel = t("asn_source_" + asn.source, asnSources.find((s) => s.value === asn.source)?.label || asn.source);

  return (
    <div
      className="bg-white rounded-xl border border-gray-200 p-5 hover:shadow-md transition-all cursor-pointer"
      onClick={() => onView(asn)}
      data-testid={`asn-card-${asn.id}`}>
      
      <div className="flex items-start justify-between mb-4">
        <div className="flex items-start gap-3">
          <div className={cn(
            "w-10 h-10 rounded-lg flex items-center justify-center",
            asn.requiresColdChain ? "bg-cyan-100" : "bg-indigo-100"
          )}>
            {asn.requiresColdChain ?
            <Thermometer className="h-5 w-5 text-cyan-600" /> :

            <Package className="h-5 w-5 text-indigo-600" />
            }
          </div>
          <div>
            <div className="flex items-center gap-2">
              <span className="font-semibold text-gray-900">{asn.id}</span>
              <Badge variant="outline" className="text-xs flex items-center gap-1">
                <SourceIcon className="h-3 w-3" />
                {sourceLabel}
              </Badge>
            </div>
            <div className="text-sm text-gray-600 mt-0.5">{asn.supplier}</div>
          </div>
        </div>
        <Badge variant="outline" className={cn("flex items-center gap-1", status.color)}>
          <StatusIcon className="h-3 w-3" />
          {statusLabel}
        </Badge>
      </div>

      <div className="grid grid-cols-3 gap-4 mb-4 text-sm">
        <div>
          <span className="text-gray-500">{t("poReference", "PO Reference")}</span>
          <div className="font-medium text-gray-900">{asn.poReference}</div>
        </div>
        <div>
          <span className="text-gray-500">{t("expected", "Expected")}</span>
          <div className="font-medium text-gray-900">{asn.expectedDate || asn.expected_date ? new Date(asn.expectedDate || asn.expected_date).toLocaleDateString() : 'N/A'}</div>
        </div>
        <div>
          <span className="text-gray-500">{t("warehouse", "Warehouse")}</span>
          <div className="font-medium text-gray-900">{asn.warehouse || '-'}</div>
        </div>
      </div>

      <div className="flex items-center justify-between pt-4 border-t border-gray-100">
        <div className="flex items-center gap-4 text-sm text-gray-600">
          <span>{asn.totalItems || (asn.items ? asn.items.length : 0)}{t("items", "items")}</span>
          <span>{(asn.totalQty || asn.total_expected || 0).toLocaleString()}{t("units", "units")}</span>
          <span className="font-medium text-gray-900">{t("r", "R$")}
            {(asn.totalValue || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
          </span>
        </div>
        <div className="flex items-center gap-2">
          {asn.status === 'ARRIVED' &&
          <Button
            size="sm"
            className="h-8 bg-cyan-600 hover:bg-cyan-700"
            onClick={(e) => {e.stopPropagation();onStartReceiving(asn);}}>
            
              <ScanLine className="h-4 w-4 mr-1" />{t("startReceiving", "Start Receiving")}

          </Button>
          }
          <ChevronRight className="h-5 w-5 text-gray-400" />
        </div>
      </div>

      {asn.dock &&
      <div className="mt-3 pt-3 border-t border-gray-100 flex items-center gap-2 text-sm text-amber-600">
          <MapPin className="h-4 w-4" />
          <span>{t("arrivedAt", "Arrived at")}{asn.dock}</span>
          {asn.arrivedAt &&
        <span className="text-gray-500">
              ({new Date(asn.arrivedAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })})
            </span>
        }
        </div>
      }

      {asn.discrepancies && asn.discrepancies.length > 0 &&
      <div className="mt-3 pt-3 border-t border-gray-100 flex items-center gap-2 text-sm text-orange-600">
          <AlertCircle className="h-4 w-4" />
          <span>{asn.discrepancies.length}{t("discrepancyiesReported", "discrepancy(ies) reported")}</span>
        </div>
      }

      {asn.requiresColdChain &&
      <div className="mt-3 pt-3 border-t border-gray-100 flex items-center gap-2 text-sm text-cyan-600">
          <Thermometer className="h-4 w-4" />
          <span>{t("coldChainRequired", "Cold chain required")}</span>
        </div>
      }
    </div>);

};

import { oigAPI } from '@/shared/services/api';

export const ASNManagement = () => {const { t } = useTranslation("oig");
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [sourceFilter, setSourceFilter] = useState('all');
  const [selectedASN, setSelectedASN] = useState(null);
  const [showDetailDialog, setShowDetailDialog] = useState(false);
  const [asns, setAsns] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchASNs = async () => {
      try {
        setLoading(true);
        const data = await oigAPI.getASNList();
        setAsns(data || []);
      } catch (err) {
        console.error('Failed to fetch ASNs:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchASNs();
  }, []);

  const filteredASNs = asns.filter((asn) => {
    const matchesSearch = String(asn.shipment_id || asn.id || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
    String(asn.supplier || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
    String(asn.poReference || '').toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = statusFilter === 'all' || asn.status === statusFilter;
    const matchesSource = sourceFilter === 'all' || asn.source === sourceFilter;
    return matchesSearch && matchesStatus && matchesSource;
  });

  const handleView = (asn) => {
    setSelectedASN(asn);
    setShowDetailDialog(true);
  };

  const handleStartReceiving = (asn) => {
    console.log('Start receiving:', asn.id);
  };

  const stats = {
    total: asns.length,
    inTransit: asns.filter((a) => a.status === 'IN_TRANSIT').length,
    arrived: asns.filter((a) => a.status === 'ARRIVED').length,
    received: asns.filter((a) => ['RECEIVED', 'RECEIVED_PARTIAL'].includes(a.status)).length
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-cyan-600" />
      </div>);

  }

  return (
    <div className="min-h-screen bg-gray-50 p-6" data-testid="asn-management-page">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">{t("asnExpectedReceipts", "ASN / Expected Receipts")}</h1>
          <p className="text-sm text-gray-500 mt-1">{t("manageAdvancedShipping", "Manage Advanced Shipping Notices and inbound shipments")}

          </p>
        </div>
        <div className="flex items-center gap-3">
          <Button variant="outline" className="gap-2">
            <Download className="h-4 w-4" />{t("export", "Export")}

          </Button>
          <Button className="gap-2 bg-cyan-600 hover:bg-cyan-700">
            <Plus className="h-4 w-4" />{t("createAsn", "Create ASN")}

          </Button>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-4 gap-4 mb-6">
        <div className="bg-white rounded-xl border border-gray-200 p-4">
          <div className="text-sm text-gray-500">{t("totalAsns", "Total ASNs")}</div>
          <div className="text-2xl font-bold text-gray-900 mt-1">{stats.total}</div>
        </div>
        <div className="bg-white rounded-xl border border-gray-200 p-4">
          <div className="text-sm text-gray-500">{t("inTransit", "In Transit")}</div>
          <div className="text-2xl font-bold text-violet-600 mt-1">{stats.inTransit}</div>
        </div>
        <div className="bg-white rounded-xl border border-gray-200 p-4">
          <div className="text-sm text-gray-500">{t("arrivedAwaiting", "Arrived (Awaiting)")}</div>
          <div className="text-2xl font-bold text-amber-600 mt-1">{stats.arrived}</div>
        </div>
        <div className="bg-white rounded-xl border border-gray-200 p-4">
          <div className="text-sm text-gray-500">{t("received", "Received")}</div>
          <div className="text-2xl font-bold text-emerald-600 mt-1">{stats.received}</div>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-xl border border-gray-200 p-4 mb-4">
        <div className="flex flex-wrap items-center gap-3">
          <div className="relative flex-1 min-w-[200px]">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
            <Input
              placeholder="Search ASNs, suppliers, PO references..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-9"
              data-testid="asn-search" />
            
          </div>
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="w-[160px]">
              <SelectValue placeholder="Status" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">{t("allStatuses", "All Statuses")}</SelectItem>
              {asnStatuses.map((s) =>
              <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>
              )}
            </SelectContent>
          </Select>
          <Select value={sourceFilter} onValueChange={setSourceFilter}>
            <SelectTrigger className="w-[140px]">
              <SelectValue placeholder="Source" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">{t("allSources", "All Sources")}</SelectItem>
              {asnSources.map((s) =>
              <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>
              )}
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* ASN Cards */}
      <div className="space-y-4">
        {filteredASNs.map((asn) =>
        <ASNCard
          key={asn.id}
          asn={asn}
          onView={handleView}
          onStartReceiving={handleStartReceiving} />

        )}
        {filteredASNs.length === 0 &&
        <div className="bg-white rounded-xl border border-gray-200 p-12 text-center">
            <Package className="h-12 w-12 text-gray-300 mx-auto mb-4" />
            <h3 className="font-medium text-gray-900">{t("noAsnsFound", "No ASNs found")}</h3>
            <p className="text-sm text-gray-500 mt-1">{t("tryAdjustingYour", "Try adjusting your filters")}</p>
          </div>
        }
      </div>

      {/* Detail Dialog */}
      <Dialog open={showDetailDialog} onOpenChange={setShowDetailDialog}>
        <DialogContent className="max-w-4xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-3">
              {selectedASN?.id}
              {selectedASN &&
              <Badge variant="outline" className={cn(statusConfig[selectedASN.status]?.color)}>
                  {asnStatuses.find((s) => s.value === selectedASN.status)?.label}
                </Badge>
              }
            </DialogTitle>
            <DialogDescription>{selectedASN?.supplier}</DialogDescription>
          </DialogHeader>

          {selectedASN &&
          <div className="space-y-4">
              <div className="grid grid-cols-3 gap-4 text-sm">
                <div className="bg-gray-50 rounded-lg p-3">
                  <span className="text-gray-500">{t("source", "Source")}</span>
                  <div className="font-medium mt-1">{asnSources.find((s) => s.value === selectedASN.source)?.label}</div>
                  <div className="text-xs text-gray-400 mt-0.5 break-all">{selectedASN.sourceRef}</div>
                </div>
                <div className="bg-gray-50 rounded-lg p-3">
                  <span className="text-gray-500">{t("poReference", "PO Reference")}</span>
                  <div className="font-medium mt-1">{selectedASN.poReference}</div>
                </div>
                <div className="bg-gray-50 rounded-lg p-3">
                  <span className="text-gray-500">{t("carrier", "Carrier")}</span>
                  <div className="font-medium mt-1">{selectedASN.carrier}</div>
                  <div className="text-xs text-gray-400 mt-0.5">{selectedASN.carrierRef}</div>
                </div>
              </div>

              <div className="border rounded-lg overflow-hidden">
                <table className="w-full text-sm">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="text-left p-3 font-medium">{t("item", "Item")}</th>
                      <th className="text-center p-3 font-medium">{t("expected", "Expected")}</th>
                      <th className="text-center p-3 font-medium">{t("received", "Received")}</th>
                      <th className="text-left p-3 font-medium">{t("lotExpiry", "Lot / Expiry")}</th>
                      <th className="text-center p-3 font-medium">{t("status", "Status")}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {selectedASN.items.map((item) =>
                  <tr key={item.id} className="border-t">
                        <td className="p-3">
                          <div className="font-medium">{item.name}</div>
                          <div className="text-gray-500 text-xs">{item.sku}</div>
                        </td>
                        <td className="p-3 text-center">{item.expectedQty}</td>
                        <td className="p-3 text-center font-medium">
                          {item.receivedQty > 0 ? item.receivedQty : '-'}
                        </td>
                        <td className="p-3">
                          {item.lot && <div className="text-xs">{item.lot}</div>}
                          {item.expiry && <div className="text-xs text-gray-500">{t("exp", "Exp:")}{item.expiry}</div>}
                          {item.coldChain &&
                      <Badge variant="outline" className="text-xs mt-1 bg-cyan-50 text-cyan-700">
                              <Thermometer className="h-3 w-3 mr-1" />
                              {item.tempRange}
                            </Badge>
                      }
                        </td>
                        <td className="p-3 text-center">
                          <Badge variant="outline" className={cn(
                        "text-xs",
                        item.status === 'RECEIVED' && "bg-emerald-50 text-emerald-700",
                        item.status === 'PARTIAL' && "bg-orange-50 text-orange-700",
                        item.status === 'PENDING' && "bg-gray-100 text-gray-600"
                      )}>
                            {item.status}
                          </Badge>
                        </td>
                      </tr>
                  )}
                  </tbody>
                </table>
              </div>

              {selectedASN.discrepancies && selectedASN.discrepancies.length > 0 &&
            <div className="bg-orange-50 border border-orange-200 rounded-lg p-4">
                  <h4 className="font-medium text-orange-800 mb-2">{t("discrepancies", "Discrepancies")}</h4>
                  {selectedASN.discrepancies.map((disc, idx) =>
              <div key={idx} className="text-sm text-orange-700">
                      <span className="font-medium">{disc.item}:</span> {disc.type}{t("expected", "- Expected")}{disc.expectedQty}{t("received", ", Received")}{disc.receivedQty}
                      <div className="text-xs text-orange-600 mt-0.5">{disc.reason}</div>
                    </div>
              )}
                </div>
            }

              {selectedASN.notes &&
            <div className="bg-gray-50 rounded-lg p-3">
                  <span className="text-sm text-gray-500">{t("notes", "Notes:")}</span>
                  <p className="text-sm mt-1">{selectedASN.notes}</p>
                </div>
            }
            </div>
          }

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDetailDialog(false)}>{t("close", "Close")}</Button>
            {selectedASN?.status === 'ARRIVED' &&
            <Button className="bg-cyan-600 hover:bg-cyan-700 gap-2">
                <ScanLine className="h-4 w-4" />{t("startReceiving", "Start Receiving")}

            </Button>
            }
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>);

};